import { X } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useState } from 'react';
import { AutoCompleteInput } from 'src/components/AutoCompleteInput';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { SelectBox } from 'src/components/SelectBox';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { SkillCard } from './SkillCard';

const speakerOptions = [
  {
    value: 'assistant',
    label: 'bot',
  },
  {
    value: 'user',
    label: 'human',
  },
];

type DispatchTarget = {
  speaker: string;
  phrase: string;
  recipient: string;
  number: string;
};

type DispatchConfig = {
  dispatch_targets?: DispatchTarget[];
};

const stubDispatchTarget: DispatchTarget = {
  speaker: 'assistant',
  phrase: '',
  recipient: '',
  number: '',
};

export const validateDispatchConfig = (
  config: DispatchConfig | undefined,
): boolean => {
  if (!config || !config.dispatch_targets) {
    return false;
  }

  return (
    config.dispatch_targets.length > 0 &&
    config.dispatch_targets.every(
      (target) =>
        !!target.speaker &&
        !!target.phrase &&
        !!target.recipient &&
        !!target.number,
    )
  );
};

export const DispatchSkill = ({
  config,
  onConfigChange,
}: {
  config: DispatchConfig;
  onConfigChange?: (config: DispatchConfig) => void;
}) => {
  const { get, remove } = useAuthedRequest();
  const [entries, setEntries] = useState<DispatchTarget[]>(
    config.dispatch_targets || [stubDispatchTarget],
  );

  const fetchLastNumbers = async (search: string) => {
    try {
      const data = await get(`/numbers/recent?number=${search}`);
      return data.map((i: { id: string; number: string }) => ({
        id: i.id,
        label: i.number,
        value: i.number,
      }));
    } catch (error) {
      console.error('Failed to fetch last numbers:', error);
      return [];
    }
  };

  return (
    <SkillCard
      description={
        <>
          Gives the agent the ability to transfer a call automatically after
          saying a specific phrase, by setting up conditions based on the
          agent’s response. <br />*
          <span className="text-primary">All fields are required.</span>
        </>
      }
    >
      {entries.map((entry, index) => (
        <div
          key={index}
          className={clsx(
            'flex py-4 rounded-md bg-neutral-50',
            index !== entries.length - 1 && 'mb-4',
          )}
        >
          <div className="flex-col gap-4 w-full pl-4">
            <div className="flex gap-2 items-center w-full">
              <span>When the</span>
              <SelectBox
                options={speakerOptions}
                className="w-[7rem] bg-neutral-0"
                value={
                  speakerOptions.find((o) => o.value === entry.speaker) ||
                  speakerOptions[0]
                }
                onChange={(value) => {
                  const updatedEntries = entries.map((e, i) =>
                    i === index ? { ...e, speaker: value } : e,
                  );
                  setEntries(updatedEntries);
                  onConfigChange?.({ dispatch_targets: updatedEntries });
                }}
              />
              <span>says</span>
              <div className="flex-1">
                <Input
                  placeholder="Phrase"
                  value={entry.phrase}
                  onChange={(evt) => {
                    const updatedEntries = entries.map((e, i) =>
                      i === index ? { ...e, phrase: evt.target.value } : e,
                    );
                    setEntries(updatedEntries);
                    onConfigChange?.({ dispatch_targets: updatedEntries });
                  }}
                  required
                />
              </div>
            </div>
            <div className="flex gap-2 mt-2 items-center w-full">
              <span>Transfer to</span>
              <div className="flex-1">
                <Input
                  placeholder="Recipient Name"
                  value={entry.recipient}
                  onChange={(evt) => {
                    const updatedEntries = entries.map((e, i) =>
                      i === index ? { ...e, recipient: evt.target.value } : e,
                    );
                    setEntries(updatedEntries);
                    onConfigChange?.({ dispatch_targets: updatedEntries });
                  }}
                  required
                />
              </div>
              <span>at</span>
              <div className="w-36">
                <AutoCompleteInput
                  id="phone-number"
                  placeholder="Recipient number"
                  value={entry.number}
                  onChange={(value) => {
                    const updatedEntries = entries.map((e, i) =>
                      i === index ? { ...e, number: value } : e,
                    );
                    setEntries(updatedEntries);
                    onConfigChange?.({ dispatch_targets: updatedEntries });
                  }}
                  onRemove={(value) => remove(`/numbers/recent/${value}`)}
                  required
                  fetchOptions={fetchLastNumbers}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center mx-2">
            <Button
              type="button"
              variant="text"
              size="small"
              onClick={() => {
                const updatedEntries = entries.filter((_, i) => i !== index);
                setEntries(updatedEntries);
                onConfigChange?.({ dispatch_targets: updatedEntries });
              }}
              className="p-2 text-neutral-400 hover:text-error"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button
        className="mt-4 ml-0"
        onClick={() => setEntries([...entries, stubDispatchTarget])}
        variant="text"
      >
        + Add more conditions
      </Button>
    </SkillCard>
  );
};
